import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XIcon,
  PlusIcon,
  SearchIcon,
  KanbanIcon,
  TableIcon,
  AlertCircleIcon,
  MapPinIcon,
  Edit3Icon,
  Trash2Icon,
  CheckSquareIcon,
  MessageSquareIcon,
  RotateCcwIcon,
  RefreshCwIcon,
  DatabaseIcon,
  CheckCircle2Icon
} from 'lucide-react';
import { issueService } from '../services/issueService';

export type IssueStatus = 'TODO' | 'IN_PROGRESS' | 'IN_REVIEW' | 'DONE';
export type IssuePriority = 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
export type IssueCategory = 'DEVICE_FAULT' | 'NETWORK' | 'SENSOR_CALIB' | 'SOFTWARE' | 'MAINTENANCE';

export interface Issue {
  id: string;
  code: string;
  title: string;
  description: string;
  status: IssueStatus;
  priority: IssuePriority;
  category: IssueCategory;
  assignee: string;
  location: string;
  createdAt: string;
  dueDate?: string;
  commentsCount?: number;
}

interface IssueBoardModalProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentLabel?: string;
  apartmentAssetId?: string;
}

const DEFAULT_ISSUES = issueService.getDefaultIssues();

const STATUS_COLUMNS: { key: IssueStatus; label: string; color: string; bg: string; border: string }[] = [
  { key: 'TODO', label: '대기 중 (To Do)', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  { key: 'IN_PROGRESS', label: '진행 중 (In Progress)', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  { key: 'IN_REVIEW', label: '검토 중 (In Review)', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  { key: 'DONE', label: '완료 (Done)', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' }
];

const CATEGORY_MAP: Record<IssueCategory, { label: string; color: string; badgeBg: string }> = {
  DEVICE_FAULT: { label: '기기 장애', color: 'text-rose-400', badgeBg: 'bg-rose-500/15 border-rose-500/30' },
  NETWORK: { label: '네트워크', color: 'text-indigo-400', badgeBg: 'bg-indigo-500/15 border-indigo-500/30' },
  SENSOR_CALIB: { label: '센서 교정', color: 'text-amber-400', badgeBg: 'bg-amber-500/15 border-amber-500/30' },
  SOFTWARE: { label: '소프트웨어', color: 'text-cyan-400', badgeBg: 'bg-cyan-500/15 border-cyan-500/30' },
  MAINTENANCE: { label: '유지보수', color: 'text-emerald-400', badgeBg: 'bg-emerald-500/15 border-emerald-500/30' }
};

const PRIORITY_MAP: Record<IssuePriority, { label: string; iconBg: string; text: string }> = {
  URGENT: { label: '긴급', iconBg: 'bg-red-500 text-white shadow-red-500/50 shadow-sm', text: 'text-red-400' },
  HIGH: { label: '높음', iconBg: 'bg-orange-500 text-white shadow-orange-500/40', text: 'text-orange-400' },
  MEDIUM: { label: '보통', iconBg: 'bg-cyan-500/80 text-white', text: 'text-cyan-400' },
  LOW: { label: '낮음', iconBg: 'bg-slate-600 text-slate-200', text: 'text-slate-400' }
};

const getPriorityInfo = (priority?: string) => {
  const p = (priority?.toUpperCase() as IssuePriority) || 'MEDIUM';
  return PRIORITY_MAP[p] || PRIORITY_MAP.MEDIUM;
};

const getCategoryInfo = (category?: string) => {
  const c = (category?.toUpperCase() as IssueCategory) || 'DEVICE_FAULT';
  return CATEGORY_MAP[c] || CATEGORY_MAP.DEVICE_FAULT;
};

export function IssueBoardModal({ isOpen, onClose, apartmentLabel, apartmentAssetId }: IssueBoardModalProps) {
  const [issues, setIssues] = useState<Issue[]>(() => {
    const saved = localStorage.getItem('sed_dashboard_issues');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { console.error(e); }
    }
    return DEFAULT_ISSUES;
  });

  const [syncStatus, setSyncStatus] = useState<'synced' | 'loading' | 'saving' | 'error'>('synced');
  const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedPriority, setSelectedPriority] = useState<string>('ALL');

  // Issue Create / Edit Modal State
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState<Partial<Issue> | null>(null);

  // Detail Modal State
  const [activeDetailIssue, setActiveDetailIssue] = useState<Issue | null>(null);

  // Load Issues from Backend Server Attributes
  const loadBackendIssues = useCallback(async () => {
    setSyncStatus('loading');
    try {
      const fetched = await issueService.fetchIssues(apartmentAssetId);
      setIssues(fetched);
      setSyncStatus('synced');
    } catch (err) {
      console.error('Failed to load issues from backend:', err);
      setSyncStatus('error');
    }
  }, [apartmentAssetId]);

  useEffect(() => {
    if (isOpen) {
      loadBackendIssues();
    }
  }, [isOpen, loadBackendIssues]);

  // Persist issues helper
  const updateAndPersistIssues = async (newIssues: Issue[]) => {
    setIssues(newIssues);
    setSyncStatus('saving');
    try {
      const ok = await issueService.saveIssues(newIssues, apartmentAssetId);
      setSyncStatus(ok ? 'synced' : 'error');
    } catch (err) {
      console.error('Failed to save issues to backend:', err);
      setSyncStatus('error');
    }
  };

  // Filtered Issues
  const filteredIssues = useMemo(() => {
    return issues.filter(issue => {
      const matchesSearch =
        issue.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        issue.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        issue.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        issue.assignee.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'ALL' || issue.category === selectedCategory;
      const matchesPriority = selectedPriority === 'ALL' || issue.priority === selectedPriority;
      return matchesSearch && matchesCategory && matchesPriority;
    });
  }, [issues, searchQuery, selectedCategory, selectedPriority]);

  // Summary Counts
  const stats = useMemo(() => {
    return {
      total: issues.length,
      todo: issues.filter(i => i.status === 'TODO').length,
      inProgress: issues.filter(i => i.status === 'IN_PROGRESS').length,
      inReview: issues.filter(i => i.status === 'IN_REVIEW').length,
      done: issues.filter(i => i.status === 'DONE').length,
      urgent: issues.filter(i => i.priority === 'URGENT').length
    };
  }, [issues]);

  const handleCreateNew = () => {
    const newCodeNum = 100 + issues.length + 1;
    setEditingIssue({
      code: `ISSUE-${newCodeNum}`,
      title: '',
      description: '',
      status: 'TODO',
      priority: 'MEDIUM',
      category: 'DEVICE_FAULT',
      assignee: '담당자 지정',
      location: apartmentLabel || '202호',
      createdAt: new Date().toISOString().slice(0, 16).replace('T', ' ')
    });
    setIsEditorModalOpen(true);
  };

  const handleEdit = (issue: Issue) => {
    setEditingIssue({ ...issue });
    setIsEditorModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('해당 이슈 항목을 삭제하시겠습니까?')) {
      const updated = issues.filter(i => i.id !== id);
      if (activeDetailIssue?.id === id) setActiveDetailIssue(null);
      await updateAndPersistIssues(updated);
    }
  };

  const handleSaveIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingIssue || !editingIssue.title) return;

    let updated: Issue[];
    if (editingIssue.id) {
      // Update
      updated = issues.map(item => item.id === editingIssue.id ? (editingIssue as Issue) : item);
    } else {
      // Create
      const newItem: Issue = {
        ...(editingIssue as Issue),
        id: Date.now().toString(),
        createdAt: new Date().toISOString().slice(0, 16).replace('T', ' ')
      };
      updated = [newItem, ...issues];
    }
    setIsEditorModalOpen(false);
    setEditingIssue(null);
    await updateAndPersistIssues(updated);
  };

  const handleStatusChange = async (issueId: string, newStatus: IssueStatus) => {
    const updated = issues.map(item => item.id === issueId ? { ...item, status: newStatus } : item);
    await updateAndPersistIssues(updated);
  };

  const handleResetToDefault = async () => {
    if (window.confirm('기본 이슈 목록으로 리셋하고 서버 DB에 저장하시겠습니까?')) {
      await updateAndPersistIssues(DEFAULT_ISSUES);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        <div key="issue-board-modal-backdrop" className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-md flex items-center justify-center p-4">
        <motion.div
          key="issue-board-modal-card"
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="w-full max-w-7xl h-[92vh] bg-[#0d1322] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden text-slate-100 font-sans"
        >
          {/* Top Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#12192c]/80 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-inner">
                <CheckSquareIcon className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold tracking-tight text-white">이슈 트래킹 보드</h2>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                    SED Issue Tracker
                  </span>
                  
                  {/* Backend DB Sync Status Badge */}
                  <div className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                    syncStatus === 'synced'
                      ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                      : syncStatus === 'loading' || syncStatus === 'saving'
                      ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300 animate-pulse'
                      : 'bg-rose-500/15 border-rose-500/30 text-rose-300'
                  }`}>
                    {syncStatus === 'loading' || syncStatus === 'saving' ? (
                      <RefreshCwIcon className="w-3 h-3 animate-spin" />
                    ) : syncStatus === 'synced' ? (
                      <DatabaseIcon className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <AlertCircleIcon className="w-3 h-3 text-rose-400" />
                    )}
                    <span>
                      {syncStatus === 'synced' && 'ThingsBoard DB 동기화 완료'}
                      {syncStatus === 'loading' && '서버 DB 불러오는 중...'}
                      {syncStatus === 'saving' && '서버 DB 저장 중...'}
                      {syncStatus === 'error' && 'DB 연동 오류 (로컬 캐시)'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  {apartmentLabel ? `${apartmentLabel} 및 시스템 현황 이슈 수집·관리 (ThingsBoard Server Attributes)` : '스마트 홈 & 디바이스 유지보수 이슈 통합 관리 (ThingsBoard Server Attributes)'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                onClick={loadBackendIssues}
                disabled={syncStatus === 'loading' || syncStatus === 'saving'}
                title="서버 DB에서 새로고침"
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white text-xs flex items-center gap-1.5 transition-all disabled:opacity-50"
              >
                <RefreshCwIcon className={`w-3.5 h-3.5 ${syncStatus === 'loading' ? 'animate-spin' : ''}`} />
                <span>새로고침</span>
              </button>
              <button
                onClick={handleResetToDefault}
                title="기본 데이터로 초기화"
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-slate-200 text-xs flex items-center gap-1.5 transition-all"
              >
                <RotateCcwIcon className="w-3.5 h-3.5" />
                <span>초기화</span>
              </button>
              <button
                onClick={handleCreateNew}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium text-xs shadow-lg shadow-cyan-500/20 transition-all"
              >
                <PlusIcon className="w-4 h-4" />
                <span>새 이슈 등록</span>
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors ml-1"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 p-4 bg-[#0a0f1a]/60 border-b border-white/5 flex-shrink-0">
            <div className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/5 flex items-center justify-between">
              <span className="text-xs text-slate-400">전체 이슈</span>
              <span className="text-base font-bold font-mono text-white">{stats.total}</span>
            </div>
            <div className="px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
              <span className="text-xs text-amber-300">대기 중</span>
              <span className="text-base font-bold font-mono text-amber-400">{stats.todo}</span>
            </div>
            <div className="px-4 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-between">
              <span className="text-xs text-cyan-300">진행 중</span>
              <span className="text-base font-bold font-mono text-cyan-400">{stats.inProgress}</span>
            </div>
            <div className="px-4 py-2.5 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-between">
              <span className="text-xs text-purple-300">검토 중</span>
              <span className="text-base font-bold font-mono text-purple-400">{stats.inReview}</span>
            </div>
            <div className="px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between">
              <span className="text-xs text-emerald-300">완료</span>
              <span className="text-base font-bold font-mono text-emerald-400">{stats.done}</span>
            </div>
            <div className="px-4 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-between">
              <span className="text-xs text-rose-300 flex items-center gap-1">
                <AlertCircleIcon className="w-3 h-3 text-rose-400" /> 긴급
              </span>
              <span className="text-base font-bold font-mono text-rose-400">{stats.urgent}</span>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-white/5 bg-[#12192c]/40 flex-shrink-0">
            {/* Search and Filters */}
            <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[300px]">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-sm">
                <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="이슈 제목, 코드, 담당자, 장소 검색..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                />
              </div>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-300 focus:outline-none focus:border-cyan-500/50 cursor-pointer"
              >
                <option value="ALL" className="bg-slate-900 text-slate-200">모든 카테고리</option>
                <option value="DEVICE_FAULT" className="bg-slate-900 text-slate-200">기기 장애</option>
                <option value="NETWORK" className="bg-slate-900 text-slate-200">네트워크</option>
                <option value="SENSOR_CALIB" className="bg-slate-900 text-slate-200">센서 교정</option>
                <option value="SOFTWARE" className="bg-slate-900 text-slate-200">소프트웨어</option>
                <option value="MAINTENANCE" className="bg-slate-900 text-slate-200">유지보수</option>
              </select>

              {/* Priority Filter */}
              <select
                value={selectedPriority}
                onChange={e => setSelectedPriority(e.target.value)}
                className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-300 focus:outline-none focus:border-cyan-500/50 cursor-pointer"
              >
                <option value="ALL" className="bg-slate-900 text-slate-200">모든 우선순위</option>
                <option value="URGENT" className="bg-slate-900 text-slate-200">긴급</option>
                <option value="HIGH" className="bg-slate-900 text-slate-200">높음</option>
                <option value="MEDIUM" className="bg-slate-900 text-slate-200">보통</option>
                <option value="LOW" className="bg-slate-900 text-slate-200">낮음</option>
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center p-1 rounded-xl bg-white/5 border border-white/5">
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  viewMode === 'kanban' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <KanbanIcon className="w-3.5 h-3.5" />
                <span>칸반 보드</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  viewMode === 'table' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <TableIcon className="w-3.5 h-3.5" />
                <span>테이블 뷰</span>
              </button>
            </div>
          </div>

          {/* Board Content */}
          <div className="flex-1 overflow-auto p-6">
            {viewMode === 'kanban' ? (
              /* Kanban Columns Grid */
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 h-full min-h-[500px]">
                {STATUS_COLUMNS.map(col => {
                  const colIssues = filteredIssues.filter(i => i.status === col.key);
                  return (
                    <div
                      key={col.key}
                      className="flex flex-col rounded-2xl bg-[#101728]/70 border border-white/5 overflow-hidden"
                    >
                      {/* Column Header */}
                      <div className={`px-4 py-3 border-b border-white/5 flex items-center justify-between ${col.bg}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${col.color.replace('text-', 'bg-')}`} />
                          <span className={`text-xs font-bold ${col.color}`}>{col.label}</span>
                        </div>
                        <span className="px-2 py-0.5 rounded-md text-[11px] font-mono font-bold bg-white/10 text-slate-200">
                          {colIssues.length}
                        </span>
                      </div>

                      {/* Cards List */}
                      <div className="flex-1 overflow-y-auto p-3 space-y-3">
                        {colIssues.length === 0 ? (
                          <div className="h-32 flex flex-col items-center justify-center text-slate-600 border border-dashed border-white/5 rounded-xl">
                            <span className="text-xs">이슈 없음</span>
                          </div>
                        ) : (
                          colIssues.map(issue => (
                            <motion.div
                              key={issue.id}
                              layoutId={`card-${issue.id}`}
                              onClick={() => setActiveDetailIssue(issue)}
                              className="p-4 rounded-xl bg-[#162035] border border-white/10 hover:border-cyan-500/40 cursor-pointer shadow-lg hover:shadow-cyan-500/10 transition-all group relative overflow-hidden"
                            >
                              {/* Top Bar: Code & Priority */}
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-mono text-cyan-400 font-bold tracking-wider">
                                  {issue.code}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getPriorityInfo(issue.priority).iconBg}`}>
                                  {getPriorityInfo(issue.priority).label}
                                </span>
                              </div>

                              {/* Title */}
                              <h3 className="text-xs font-bold text-slate-100 group-hover:text-cyan-300 transition-colors line-clamp-2 mb-2 leading-relaxed">
                                {issue.title}
                              </h3>

                              {/* Category Badge */}
                              <div className="mb-3">
                                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium border ${getCategoryInfo(issue.category).badgeBg} ${getCategoryInfo(issue.category).color}`}>
                                  {getCategoryInfo(issue.category).label}
                                </span>
                              </div>

                              {/* Footer Info */}
                              <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[11px] text-slate-400">
                                <div className="flex items-center gap-1 truncate max-w-[140px]">
                                  <MapPinIcon className="w-3 h-3 text-slate-500" />
                                  <span className="truncate">{issue.location}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {issue.commentsCount && issue.commentsCount > 0 ? (
                                    <span className="flex items-center gap-0.5 text-[10px] text-slate-500">
                                      <MessageSquareIcon className="w-3 h-3" />
                                      {issue.commentsCount}
                                    </span>
                                  ) : null}
                                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-[9px] font-bold text-cyan-300">
                                    {issue.assignee ? issue.assignee.slice(0, 1) : 'U'}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Table View */
              <div className="rounded-xl border border-white/10 bg-[#101728]/80 overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
                      <th className="px-4 py-3">코드</th>
                      <th className="px-4 py-3">제목</th>
                      <th className="px-4 py-3">상태</th>
                      <th className="px-4 py-3">우선순위</th>
                      <th className="px-4 py-3">카테고리</th>
                      <th className="px-4 py-3">장소</th>
                      <th className="px-4 py-3">담당자</th>
                      <th className="px-4 py-3">생성일</th>
                      <th className="px-4 py-3 text-right">작업</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 text-xs text-slate-300 font-roboto">
                    {filteredIssues.map(issue => (
                      <tr key={issue.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3 font-mono font-bold text-cyan-400">{issue.code}</td>
                        <td className="px-4 py-3 font-medium text-white max-w-xs truncate cursor-pointer hover:text-cyan-300" onClick={() => setActiveDetailIssue(issue)}>
                          {issue.title}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={issue.status}
                            onChange={e => handleStatusChange(issue.id, e.target.value as IssueStatus)}
                            className="bg-slate-900 border border-white/10 text-slate-200 rounded px-2 py-1 text-[11px] focus:outline-none"
                          >
                            <option value="TODO">대기 중</option>
                            <option value="IN_PROGRESS">진행 중</option>
                            <option value="IN_REVIEW">검토 중</option>
                            <option value="DONE">완료</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getPriorityInfo(issue.priority).iconBg}`}>
                            {getPriorityInfo(issue.priority).label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] border ${getCategoryInfo(issue.category).badgeBg} ${getCategoryInfo(issue.category).color}`}>
                            {getCategoryInfo(issue.category).label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400">{issue.location}</td>
                        <td className="px-4 py-3">{issue.assignee}</td>
                        <td className="px-4 py-3 text-slate-500">{issue.createdAt}</td>
                        <td className="px-4 py-3 text-right space-x-2">
                          <button onClick={() => handleEdit(issue)} className="p-1 hover:text-cyan-400 transition-colors">
                            <Edit3Icon className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(issue.id)} className="p-1 hover:text-rose-400 transition-colors">
                            <Trash2Icon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      </div>
      </AnimatePresence>

      {/* Editor Modal (Create / Edit) */}
      <AnimatePresence>
        {isEditorModalOpen && editingIssue && (
          <div key="issue-editor-modal-backdrop" className="fixed inset-0 z-[140] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
            <motion.div
              key="issue-editor-modal-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="w-full max-w-xl bg-[#12192c] border border-white/15 rounded-2xl p-6 shadow-2xl text-slate-100 font-sans"
            >
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-white/10">
              <h3 className="text-base font-bold text-white">
                {editingIssue.id ? '이슈 수정' : '새 이슈 등록'}
              </h3>
              <button onClick={() => setIsEditorModalOpen(false)} className="text-slate-400 hover:text-white">
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveIssue} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-400 mb-1 font-medium">이슈 제목 *</label>
                <input
                  type="text"
                  required
                  placeholder="예: 202호 거실 온도센서 텔레메트리 유실"
                  value={editingIssue.title || ''}
                  onChange={e => setEditingIssue(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-200 focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">카테고리</label>
                  <select
                    value={editingIssue.category || 'DEVICE_FAULT'}
                    onChange={e => setEditingIssue(prev => ({ ...prev, category: e.target.value as IssueCategory }))}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-slate-200 focus:outline-none"
                  >
                    <option value="DEVICE_FAULT">기기 장애</option>
                    <option value="NETWORK">네트워크</option>
                    <option value="SENSOR_CALIB">센서 교정</option>
                    <option value="SOFTWARE">소프트웨어</option>
                    <option value="MAINTENANCE">유지보수</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">우선순위</label>
                  <select
                    value={editingIssue.priority || 'MEDIUM'}
                    onChange={e => setEditingIssue(prev => ({ ...prev, priority: e.target.value as IssuePriority }))}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-slate-200 focus:outline-none"
                  >
                    <option value="URGENT">긴급</option>
                    <option value="HIGH">높음</option>
                    <option value="MEDIUM">보통</option>
                    <option value="LOW">낮음</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">상태</label>
                  <select
                    value={editingIssue.status || 'TODO'}
                    onChange={e => setEditingIssue(prev => ({ ...prev, status: e.target.value as IssueStatus }))}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-slate-200 focus:outline-none"
                  >
                    <option value="TODO">대기 중 (To Do)</option>
                    <option value="IN_PROGRESS">진행 중 (In Progress)</option>
                    <option value="IN_REVIEW">검토 중 (In Review)</option>
                    <option value="DONE">완료 (Done)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">담당자</label>
                  <input
                    type="text"
                    placeholder="담당자 이름"
                    value={editingIssue.assignee || ''}
                    onChange={e => setEditingIssue(prev => ({ ...prev, assignee: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-200 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">관련 장소 / 기기</label>
                <input
                  type="text"
                  placeholder="예: 202호 거실 / SEO_1 센서"
                  value={editingIssue.location || ''}
                  onChange={e => setEditingIssue(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-200 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">상세 내용 및 설명</label>
                <textarea
                  rows={4}
                  placeholder="발생한 이슈의 구체적인 상황 및 조치 요청 내용을 적어주세요."
                  value={editingIssue.description || ''}
                  onChange={e => setEditingIssue(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-200 focus:outline-none resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setIsEditorModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 font-medium"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-bold shadow-lg shadow-cyan-500/20"
                >
                  저장하기
                </button>
              </div>
            </form>
          </motion.div>
        </div>
        )}
      </AnimatePresence>

      {/* Detail View Modal */}
      <AnimatePresence>
        {activeDetailIssue && (
          <div key="issue-detail-modal-backdrop" className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
            <motion.div
              key="issue-detail-modal-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="w-full max-w-2xl bg-[#12192c] border border-white/15 rounded-2xl p-6 shadow-2xl text-slate-100 font-sans"
            >
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono font-bold text-cyan-400">{activeDetailIssue.code}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getPriorityInfo(activeDetailIssue.priority).iconBg}`}>
                  {getPriorityInfo(activeDetailIssue.priority).label}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] border ${getCategoryInfo(activeDetailIssue.category).badgeBg} ${getCategoryInfo(activeDetailIssue.category).color}`}>
                  {getCategoryInfo(activeDetailIssue.category).label}
                </span>
              </div>
              <button onClick={() => setActiveDetailIssue(null)} className="text-slate-400 hover:text-white">
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="py-4 space-y-4">
              <h2 className="text-lg font-bold text-white">{activeDetailIssue.title}</h2>

              <div className="grid grid-cols-2 gap-4 p-3 rounded-xl bg-white/5 border border-white/5 text-xs text-slate-300">
                <div>
                  <span className="text-slate-500 block mb-1">상태</span>
                  <select
                    value={activeDetailIssue.status}
                    onChange={e => {
                      const newStatus = e.target.value as IssueStatus;
                      handleStatusChange(activeDetailIssue.id, newStatus);
                      setActiveDetailIssue(prev => prev ? { ...prev, status: newStatus } : null);
                    }}
                    className="bg-slate-900 border border-white/10 text-slate-200 rounded px-2 py-1 text-xs"
                  >
                    <option value="TODO">대기 중 (To Do)</option>
                    <option value="IN_PROGRESS">진행 중 (In Progress)</option>
                    <option value="IN_REVIEW">검토 중 (In Review)</option>
                    <option value="DONE">완료 (Done)</option>
                  </select>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">담당자</span>
                  <span className="font-medium text-cyan-300">{activeDetailIssue.assignee}</span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">관련 장소/기기</span>
                  <span className="font-medium">{activeDetailIssue.location}</span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">등록일</span>
                  <span className="font-mono text-slate-400">{activeDetailIssue.createdAt}</span>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold text-slate-400 mb-2">상세 설명</h4>
                <div className="p-4 rounded-xl bg-[#0a0f1a] border border-white/5 text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {activeDetailIssue.description || '상세 설명이 없습니다.'}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <button
                onClick={() => handleDelete(activeDetailIssue.id)}
                className="px-3 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs flex items-center gap-1.5 transition-colors"
              >
                <Trash2Icon className="w-3.5 h-3.5" />
                <span>이슈 삭제</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const target = activeDetailIssue;
                    setActiveDetailIssue(null);
                    handleEdit(target);
                  }}
                  className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-slate-200 text-xs font-medium"
                >
                  수정하기
                </button>
                <button
                  onClick={() => setActiveDetailIssue(null)}
                  className="px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-bold"
                >
                  확인
                </button>
              </div>
            </div>
          </motion.div>
        </div>
        )}
      </AnimatePresence>
    </>
  );
}
